/**
 * Los gastos del período, por categoría (0156).
 *
 * Al mirar el mes, lo mal cargado salta a la vista: en noviembre había
 * $ 1,2 M en «Otro», entre ellos un pago a un proveedor de $ 875.000. «Otro»
 * se marca cuando pasa de $ 50.000 o del 10 % de los gastos.
 */
import { TriangleAlert } from "lucide-react";

import { Importe } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Card } from "@/components/ui/card";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { ETIQUETA_CONCEPTO } from "@/components/caja/logica";
import { aNumero, type GastoPorCategoria } from "@/lib/tipos";
import { cn } from "@/lib/utils";

/** Desde cuanto «Otro» pide atención. */
export function otroLlamativo(otro: number, total: number): boolean {
  return otro >= 50_000 || (total > 0 && otro / total >= 0.1);
}

export function GastosPorCategoria({
  filas,
}: {
  filas: ReadonlyArray<GastoPorCategoria>;
}): JSX.Element {
  const total = filas.reduce((a, f) => a + aNumero(f.total_ars), 0);
  if (filas.length === 0 || total <= 0) {
    return <EstadoVacio titulo="No hay gastos cargados en este período." />;
  }
  const otro = aNumero(filas.find((f) => f.concepto === "OTRO")?.total_ars ?? 0);
  const marcarOtro = otroLlamativo(otro, total);

  return (
    <div className="space-y-3">
      {marcarOtro ? (
        <Aviso tono="warning" icono={TriangleAlert} compacto>
          Hay <Importe monto={otro} moneda="ARS" tamano="sm" codigo={false} /> en «Otro». Revisá que
          no sean pagos a proveedores o retiros: esos no son gastos del local.
        </Aviso>
      ) : null}
      <Card padding="compacto">
        <ul className="divide-y divide-border">
          {filas.map((f) => {
            const monto = aNumero(f.total_ars);
            const pct = total > 0 ? Math.round((monto / total) * 100) : 0;
            const esOtro = f.concepto === "OTRO";
            return (
              <li key={f.concepto} className="space-y-1 py-2">
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className={cn(esOtro && marcarOtro && "font-medium text-warning")}>
                    {ETIQUETA_CONCEPTO[f.concepto] ?? f.concepto}
                    <span className="pl-2 text-xs text-muted-foreground">
                      {f.cantidad} {f.cantidad === 1 ? "movimiento" : "movimientos"} · {pct} %
                    </span>
                  </span>
                  <Importe monto={-monto} moneda="ARS" tamano="sm" codigo={false} />
                </div>
                <div className="h-1.5 rounded-full bg-muted">
                  <div
                    className={cn(
                      "h-1.5 rounded-full",
                      esOtro && marcarOtro ? "bg-warning" : "bg-primary",
                    )}
                    style={{ width: `${Math.max(pct, 1)}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
        <div className="flex items-baseline justify-between gap-3 border-t border-border pt-2 text-sm font-medium">
          <span>Total de gastos</span>
          <Importe monto={-total} moneda="ARS" tamano="sm" codigo={false} />
        </div>
      </Card>
    </div>
  );
}
